import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, PackageCheck } from "lucide-react";
import { getProductStoreMatches } from "../services/ProductsService";
import {
  type RetailerType,
} from "../config/retailers";
import { getStoreById } from "../utils/storeUtils";
import {
  formatLastUpdated,
  getLastUpdatedTimestamp,
} from "./ProductItem.utils";
import {
  getLatestStoreInventory,
  getLatestStorePrice,
  isAvailableAtStore,
} from "./OtherStoreAvailability.utils";

interface OtherStoreAvailabilityProps {
  productIdentifier: string;
  currentStoreId: string;
  retailer: string;
}

type PhysicalRetailer = Extract<
  RetailerType,
  | "walmart"
  | "home_depot"
  | "target"
  | "dollar_general"
  | "bestbuy"
  | "costco"
  | "lowes"
>;

const RETAILERS_WITH_CONFIGURED_STORES = new Set<PhysicalRetailer>([
  "walmart",
  "home_depot",
  "target",
  "dollar_general",
  "bestbuy",
  "costco",
  "lowes",
]);

function formatPrice(price: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(price);
}

function getStoreLocation(storeId: string, retailer: PhysicalRetailer): string {
  const store = getStoreById(storeId, retailer);
  if (!store) return "";

  return (
    [store.city, store.state].filter(Boolean).join(", ") ||
    store.address ||
    ""
  );
}

export default function OtherStoreAvailability({
  productIdentifier,
  currentStoreId,
  retailer,
}: OtherStoreAvailabilityProps) {
  const retailerType = RETAILERS_WITH_CONFIGURED_STORES.has(
    retailer as PhysicalRetailer
  )
    ? (retailer as PhysicalRetailer)
    : null;
  const showHotDeals = currentStoreId.endsWith("-CRAZY-DEALS");
  const showPennyItems = currentStoreId.endsWith("-PENNY-ITEM");
  const showCoupons = currentStoreId.endsWith("-COUPON");

  const { data, isLoading } = useQuery({
    queryKey: [
      "product-store-matches",
      retailerType,
      productIdentifier,
      showHotDeals,
      showPennyItems,
      showCoupons,
    ],
    queryFn: () =>
      getProductStoreMatches({
        retailer: retailerType!,
        productId: productIdentifier,
        showHotDeals,
        showPennyItems,
        ...(showCoupons ? { showCoupons: true } : {}),
      }),
    enabled: !!retailerType && !!productIdentifier,
    retry: false,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const normalizedCurrentStoreId = currentStoreId
    .replace(/-CRAZY-DEALS$/, "")
    .replace(/-PENNY-ITEM$/, "")
    .replace(/-COUPON$/, "");
  const availableProducts = (data?.products || []).filter(
    (product) =>
      product.storeId
        .replace(/-CRAZY-DEALS$/, "")
        .replace(/-PENNY-ITEM$/, "")
        .replace(/-COUPON$/, "") !== normalizedCurrentStoreId &&
      (showPennyItems || isAvailableAtStore(product))
  );

  if (!retailerType) return null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-3 py-2.5 text-xs text-gray-500 dark:text-dark-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking your other configured stores…
      </div>
    );
  }

  if (availableProducts.length === 0) return null;

  return (
    <section className="rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-900/10 overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-emerald-200 dark:border-emerald-800/60 px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <PackageCheck className="h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-xs sm:text-sm font-semibold text-emerald-900 dark:text-emerald-200 truncate">
            {showPennyItems
              ? "Possible stock at your other stores"
              : "Available at your other stores"}
          </h3>
        </div>
        <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-[10px] sm:text-xs font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
          {availableProducts.length}
        </span>
      </div>

      <div className="divide-y divide-emerald-200/80 dark:divide-emerald-800/50">
        {availableProducts.map((otherProduct) => {
          const inventory = getLatestStoreInventory(otherProduct);
          const price = getLatestStorePrice(otherProduct);
          const location = getStoreLocation(otherProduct.storeId, retailerType);
          const lastUpdatedTimestamp = getLastUpdatedTimestamp({
            priceHistory: otherProduct.storePrice,
            backroomHistory: otherProduct.backroom,
            salesFloorHistory: otherProduct.salesFloor,
            lastUpdates: otherProduct.lastUpdates,
            firstInserted: otherProduct.firstInserted,
            lastUpdated: otherProduct.lastUpdated,
          });
          const lastUpdatedLabel = formatLastUpdated([lastUpdatedTimestamp], {
            emptyLabel: "N/A",
          });
          const lastRegularTimestamp =
            otherProduct.lastUpdates?.[otherProduct.lastUpdates.length - 1] ||
            otherProduct.firstInserted;
          const lastRegularLabel = formatLastUpdated([lastRegularTimestamp], {
            emptyLabel: "N/A",
          });
          const displayStoreId =
            retailerType === "costco"
              ? otherProduct.storeId.replace(/-wh$/, "")
              : otherProduct.storeId.replace(/-PENNY-ITEM$/, "");

          return (
            <div
              key={otherProduct.storeId}
              className="flex items-center gap-3 px-3 py-2.5 bg-white/60 dark:bg-dark-800/40"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                <MapPin className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                    {retailerType === "costco" ? "Warehouse" : "Store"} #{displayStoreId}
                  </span>
                  {otherProduct.aisle && retailerType !== "costco" && (
                    <span className="rounded bg-gray-100 dark:bg-dark-700 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:text-dark-300">
                      Aisle {otherProduct.aisle}
                    </span>
                  )}
                </div>
                {location && (
                  <p className="mt-0.5 truncate text-[10px] sm:text-xs text-gray-500 dark:text-dark-400">
                    {location}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-xs sm:text-sm font-bold tabular-nums text-emerald-800 dark:text-emerald-300">
                  {formatPrice(price)}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-400">
                  {showPennyItems
                    ? "Possible stock"
                    : inventory > 0
                      ? `${inventory} units`
                      : "In stock"}
                </div>
                <div className="mt-0.5 text-[9px] sm:text-[10px] text-gray-400 dark:text-dark-500 whitespace-nowrap">
                  {showPennyItems
                    ? `Last regular ${lastRegularLabel}`
                    : `Updated ${lastUpdatedLabel}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
